// 기간 손익 상세 보기 — 풀스크린 페이지
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchPeriodDetail, fetchPeriodEval } from '../api/mobileApi';
import '../styles/mobile.css';

const fNum   = v => (v == null ? '-' : Math.round(Number(v)).toLocaleString('ko-KR'));
const fSign  = v => (Number(v) > 0 ? '+' : '');
const colorCls = v => (Number(v) > 0 ? 'pos' : Number(v) < 0 ? 'neg' : 'neu');
const fQty   = v =>
  v == null ? '-' : Number(v).toLocaleString('ko-KR', { maximumFractionDigits: 4 });

// ── 기간 평가손익 탭 ────────────────────────────────────────
function EvalTab({ items }) {
  if (!items) return <div className="mb-loading">불러오는 중...</div>;
  if (!items.length) return <div className="mb-empty">해당 기간의 평가손익 데이터가 없습니다.</div>;

  const acctOrder = [];
  const acctMap   = {};
  items.forEach(r => {
    if (!acctMap[r.account_name]) {
      acctMap[r.account_name] = [];
      acctOrder.push(r.account_name);
    }
    acctMap[r.account_name].push(r);
  });

  return (
    <>
      {acctOrder.map(acct => {
        const rows      = acctMap[acct];
        const subProfit = rows.reduce((s, r) => s + (r.eval_profit ?? 0), 0);
        return (
          <React.Fragment key={acct}>
            <div className="mb-acct-header">
              <span className="mb-acct-header-name">{acct}</span>
              <span className={`mb-acct-header-sub ${colorCls(subProfit)}`}>
                소계 {fSign(subProfit)}{fNum(subProfit)}원
              </span>
            </div>
            {rows.map((r, i) => {
              const ep = r.eval_profit ?? 0;
              const startMeta = `기초 ${fQty(r.start_quantity)}주 × ${fNum(r.start_price)}원 = ${fNum(r.start_amount)}원`;
              const endMeta   = `기말 ${fQty(r.end_quantity)}주 × ${fNum(r.end_price)}원 = ${fNum(r.end_amount)}원`;
              return (
                <div key={i} className="mb-stock-item" style={{ alignItems: 'flex-start', paddingTop: 10 }}>
                  <div className="mb-stock-item-left">
                    <div className="mb-stock-name">{r.stock_name}</div>
                    <div className="mb-stock-meta">{startMeta}</div>
                    <div className="mb-stock-meta">{endMeta}</div>
                  </div>
                  <div className="mb-stock-item-right">
                    <div className={`mb-stock-profit ${colorCls(ep)}`}>
                      {ep !== 0 ? `${fSign(ep)}${fNum(ep)}원` : '-'}
                    </div>
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        );
      })}
      <div className="mb-subtotal-row" style={{ background: '#eef2ff' }}>
        <span style={{ fontSize: 13, color: '#555' }}>전체 합계</span>
        <span className={`mb-subtotal-profit ${colorCls(items.reduce((s,r) => s + (r.eval_profit ?? 0), 0))}`}
              style={{ fontSize: 15 }}>
          {fSign(items.reduce((s,r) => s + (r.eval_profit ?? 0), 0))}
          {fNum(items.reduce((s,r) => s + (r.eval_profit ?? 0), 0))}원
        </span>
      </div>
      <div style={{ padding: '10px 14px', fontSize: 11, color: '#bbb' }}>
        ℹ 종목 매도 시 매도원금 만큼 평가손익에서 제외됩니다.
      </div>
    </>
  );
}

// ── 매도 확정손익 탭 ────────────────────────────────────────
function SellTab({ data }) {
  if (!data) return <div className="mb-loading">불러오는 중...</div>;
  if (!data.length) return <div className="mb-empty">해당 기간의 매도 내역이 없습니다.</div>;

  const acctOrder = [];
  const acctMap   = {};
  data.forEach(r => {
    if (!acctMap[r.account_name]) {
      acctMap[r.account_name] = [];
      acctOrder.push(r.account_name);
    }
    acctMap[r.account_name].push(r);
  });

  const total = data.reduce((s, r) => s + (r.confirmed_profit ?? 0), 0);
  return (
    <>
      {acctOrder.map(acct => {
        const rows = acctMap[acct];
        const sub  = rows.reduce((s, r) => s + (r.confirmed_profit ?? 0), 0);
        return (
          <React.Fragment key={acct}>
            <div className="mb-acct-header">
              <span className="mb-acct-header-name">{acct}</span>
              <span className={`mb-acct-header-sub ${colorCls(sub)}`}>
                소계 {fSign(sub)}{fNum(sub)}원
              </span>
            </div>
            {rows.map((r, i) => {
              const cp = r.confirmed_profit ?? 0;
              const pr = r.profit_rate ?? 0;
              return (
                <div key={i} className="mb-stock-item" style={{ alignItems: 'flex-start', paddingTop: 10 }}>
                  <div className="mb-stock-item-left">
                    <div className="mb-stock-name">{r.stock_name}</div>
                    <div className="mb-stock-meta">
                      매도일 {r.sell_date?.slice(0, 10) || '-'}
                    </div>
                    <div className="mb-stock-meta">
                      매수 {fNum(r.buy_price)}원 → 매도 {fNum(r.sell_price)}원
                      · {fQty(r.quantity)}주
                    </div>
                  </div>
                  <div className="mb-stock-item-right">
                    <div className={`mb-stock-profit ${colorCls(cp)}`}>
                      {fSign(cp)}{fNum(cp)}원
                    </div>
                    <div className={`mb-stock-rate ${colorCls(pr)}`}>
                      {fSign(pr)}{Number(pr).toFixed(2)}%
                    </div>
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        );
      })}
      <div className="mb-subtotal-row" style={{ background: '#eef2ff' }}>
        <span style={{ fontSize: 13, color: '#555' }}>전체 합계</span>
        <span className={`mb-subtotal-profit ${colorCls(total)}`} style={{ fontSize: 15 }}>
          {fSign(total)}{fNum(total)}원
        </span>
      </div>
    </>
  );
}

// ── 분배금/이자 탭 ───────────────────────────────────────────
function DivTab({ data }) {
  if (!data) return <div className="mb-loading">불러오는 중...</div>;
  if (!data.length) return <div className="mb-empty">해당 기간의 분배금 내역이 없습니다.</div>;
  const total = data.reduce((s, r) => s + (r.div_amount ?? 0), 0);
  return (
    <>
      {data.map((r, i) => (
        <div key={i} className="mb-stock-item">
          <div className="mb-stock-item-left">
            <div className="mb-stock-name">{r.stock_name}</div>
            <div className="mb-stock-meta">{r.account_name} · {r.ym}</div>
          </div>
          <div className="mb-stock-item-right">
            <div className="mb-stock-profit pos">{fNum(r.div_amount)}원</div>
          </div>
        </div>
      ))}
      <div className="mb-subtotal-row" style={{ background: '#eef2ff' }}>
        <span style={{ fontSize: 13, color: '#555' }}>전체 합계</span>
        <span className="mb-subtotal-profit pos" style={{ fontSize: 15 }}>
          {fNum(total)}원
        </span>
      </div>
    </>
  );
}


// ════════════════════════════════════════════════════════════
// 메인 컴포넌트
// ════════════════════════════════════════════════════════════
export default function DashboardPeriodDetailPage() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const accounts     = state?.accounts     ?? [];
  const periodParams = state?.periodParams ?? { period: 'today', date: new Date().toISOString().slice(0, 10) };
  const periodLabel  = state?.periodLabel  ?? '';

  const acctParam = accounts.join(',');

  const [tab,        setTab]        = useState('eval');  // 'eval' | 'sell' | 'div'
  const [detail,     setDetail]     = useState(null);
  const [evalItems,  setEvalItems]  = useState(null);
  const [detailLoad, setDetailLoad] = useState(false);
  const [evalLoad,   setEvalLoad]   = useState(false);

  // period-detail 한 번만 로드
  useEffect(() => {
    setDetailLoad(true);
    fetchPeriodDetail(acctParam, periodParams)
      .then(d => setDetail(d))
      .catch(() => setDetail({ sell_list: [], dividend_list: [] }))
      .finally(() => setDetailLoad(false));
  }, []); // eslint-disable-line

  // period-eval은 탭 활성화 시 로드
  useEffect(() => {
    if (tab !== 'eval') return;
    if (evalItems !== null) return;
    setEvalLoad(true);
    fetchPeriodEval(acctParam, periodParams)
      .then(d => setEvalItems(d?.items ?? []))
      .catch(() => setEvalItems([]))
      .finally(() => setEvalLoad(false));
  }, [tab]); // eslint-disable-line

  const TAB_DEFS = [
    { v: 'eval', l: '기간평가손익' },
    { v: 'sell', l: '매도확정손익' },
    { v: 'div',  l: '분배금·이자' },
  ];

  return (
    <div className="mb-root mb-back-layout">
      {/* 헤더 */}
      <div className="mb-back-header">
        <div className="mb-back-header-row">
          <button className="mb-back-btn" onClick={() => navigate(-1)}>‹</button>
          <span className="mb-back-header-title">기간 손익 상세</span>
        </div>
        {periodLabel && (
          <div className="mb-back-header-sub">📅 {periodLabel} 기준</div>
        )}
      </div>

      {/* 탭 */}
      <div className="mb-detail-tabs">
        {TAB_DEFS.map(t => (
          <button
            key={t.v}
            className={`mb-detail-tab${tab === t.v ? ' active' : ''}`}
            onClick={() => setTab(t.v)}
          >
            {t.l}
          </button>
        ))}
      </div>

      {/* 본문 */}
      <div className="mb-back-content" key={tab}>
        {tab === 'eval' && (
          evalLoad ? <div className="mb-loading">불러오는 중...</div>
                   : <EvalTab items={evalItems} />
        )}
        {tab === 'sell' && (
          detailLoad ? <div className="mb-loading">불러오는 중...</div>
                     : <SellTab data={detail?.sell_list} />
        )}
        {tab === 'div' && (
          detailLoad ? <div className="mb-loading">불러오는 중...</div>
                     : <DivTab data={detail?.dividend_list} />
        )}
        <div style={{ height: 24 }} />
      </div>
    </div>
  );
}
